/**
 * RendererRow — role base for rows whose job is to fill a specific
 * layout slot (`canvas.*`, `column.*`, `timeline[row].*`,
 * `timeline[column].*`, `grid.*`).
 *
 * Renderer attributes (color, offset, radius, type, …) live in the
 * row's shared `attributes` bag on `RowMetadataStore` — there's no
 * dedicated renderer-attr store. Access goes through
 * `this.attributes()` / `attributesAs(decode)` (inherited from
 * `ModelRow`); writes go
 * through `rowMetadata.patchAttributes` so consumers get one
 * `RowDataChanged(rowId, field)` per changed key.
 */

import type { JsonObject } from "../../core/json.ts";
import { EMPTY_FROZEN_OBJECT } from "../../core/frozen-copy.ts";
import type { RendererDescriptor } from "../descriptor.ts";
import { type RowLocalId, isSyntheticDefaultRendererId } from "../../ids/row-local-id.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import { sheetOf, sheetStores } from "../../sheet/sheet-stores.ts";
import { ModelRow } from "../model-row.ts";
import type { PropertyListProjection } from "../../render/tree-projection.ts";
import { SourceRow } from "./source-row.ts";
import type { Option } from "../../core/option.ts";

/**
 * Shape of a renderer row's attribute bag: JSON. Each concrete
 * renderer subclass narrows the keys it expects with the `Json`
 * readers (`Json.string(this.attrs(), "color")`).
 */
export type RendererAttrs = JsonObject;

/** The base of a row that renders its parent source row's data. */
export class RendererRow extends ModelRow {
  declare readonly descriptor: RendererDescriptor;

  constructor(
    id: RowLocalId,
    descriptor: RendererDescriptor,
    sheet: ModelSheet,
  ) {
    super(id, descriptor, sheet);
  }

  /**
   * Auto-hydrated default renderers are `"omit"`ted from the `default`
   * projection — they're plumbing that paints the source's slot, so the
   * source reads as a clean leaf. Explicitly-authored renderers stay
   * visible (`"branch"`). `full` mode shows both.
   */
  override get defaultProjection(): PropertyListProjection.DefaultProjection {
    return isSyntheticDefaultRendererId(this.id) ? "omit" : "branch";
  }

  /**
   * The row whose data this renderer shows (row-subsumption.md rule 2): its
   * container when that is a source, else the renderer row itself.
   */
  get dataRow(): ModelRow {
    return this.parent instanceof SourceRow ? this.parent : this;
  }

  /** The lookup key of that data: the source's, or one the renderer row itself carries; `null` when none. */
  get dataAddress(): Option<string> {
    const dataRow = this.dataRow;
    return dataRow.sheetReads.rowMeta(dataRow.id).lookupKey ?? null;
  }

  /**
   * Whether the renderer is bound to data: it sits in a source, or carries a
   * data key of its own. Reported the same way in every container; a
   * renderer bound to no data is a row of empty cells under its own label
   * (else its kind's name): no data is synthesized for it
   * (sheets/.docs/content/row-subsumption.md).
   */
  get isBound(): boolean {
    return this.dataRow !== this || this.dataAddress != null;
  }

  /** Current renderer attributes (color, offset, …). */
  attrs(): RendererAttrs {
    return this.attributes() ?? EMPTY_FROZEN_OBJECT;
  }

  override toDebug(): Record<string, unknown> {
    const a = this.attrs();
    return {
      ...super.toDebug(),
      rendererId: this.descriptor.rendererId,
      ...(Object.keys(a).length > 0 ? { attrs: a } : {}),
    };
  }
}
